import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getAccessToken } from "@/lib/api";

/**
 * Maintains the notification WebSocket.
 *
 * The socket carries only hints ("you have a new notification"); the REST API
 * remains the source of truth (mirrors the server design). So on any inbound
 * message we simply invalidate the notification queries and let React Query
 * refetch — no client-side merging, nothing to get out of sync.
 *
 * Reconnects with capped backoff. The access token is sent in the first frame,
 * never in the URL, so it never lands in a proxy or server log.
 */
export function useNotificationSocket(enabled: boolean): void {
  const qc = useQueryClient();
  const wsRef = useRef<WebSocket | null>(null);
  const retryRef = useRef(0);
  const closedRef = useRef(false);
  const pingRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!enabled) return;
    closedRef.current = false;

    function connect() {
      const token = getAccessToken();
      if (!token) {
        // No token yet (still restoring session); try again shortly.
        scheduleReconnect();
        return;
      }

      const proto = window.location.protocol === "https:" ? "wss" : "ws";
      const ws = new WebSocket(`${proto}://${window.location.host}/ws/v1/connect`);
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(JSON.stringify({ type: "auth", token }));
        // Keepalive: without periodic traffic an idle socket can be silently
        // dropped by a proxy or the browser, which is a common cause of chat
        // appearing to stop updating. The server replies with a pong.
        window.clearInterval(pingRef.current);
        pingRef.current = window.setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }, 25_000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          retryRef.current = 0;
          // One socket carries every event on the user's channel. Route by
          // type: notifications and chat both refresh their query (the DB is
          // the source of truth); typing is ephemeral and fanned out as a DOM
          // event the open chat panel listens for.
          if (data.type === "notification") {
            void qc.invalidateQueries({ queryKey: ["notifications"] });
          } else if (data.type === "chat_message" || data.type === "chat_read") {
            void qc.invalidateQueries({ queryKey: ["chat", data.room_id] });
            void qc.invalidateQueries({ queryKey: ["chat-unread"] });
          } else if (data.type === "chat_typing") {
            window.dispatchEvent(new CustomEvent("chat:typing", { detail: data }));
          }
        } catch {
          /* ignore malformed frames */
        }
      };

      ws.onclose = () => {
        wsRef.current = null;
        window.clearInterval(pingRef.current);
        if (!closedRef.current) scheduleReconnect();
      };

      ws.onerror = () => {
        ws.close();
      };
    }

    function scheduleReconnect() {
      // 1s, 2s, 4s … capped at 30s. Avoids hammering a server that is down.
      const delay = Math.min(30_000, 1000 * 2 ** retryRef.current);
      retryRef.current += 1;
      window.setTimeout(() => {
        if (!closedRef.current) connect();
      }, delay);
    }

    connect();

    return () => {
      closedRef.current = true;
      window.clearInterval(pingRef.current);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [enabled, qc]);
}
